import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Search, X } from "lucide-react";
import { searchCandidates } from "@/lib/api";

export function GlobalSearch() {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);
  const seq = useRef(0);

  const run = useCallback((term) => {
    const t = term.trim();
    if (!t) { setResults([]); setLoading(false); return; }
    const my = ++seq.current;
    setLoading(true);
    searchCandidates(t)
      .then((r) => { if (my === seq.current) setResults(r); })
      .catch(() => { if (my === seq.current) setResults([]); })
      .finally(() => { if (my === seq.current) setLoading(false); });
  }, []);

  useEffect(() => {
    const id = setTimeout(() => run(q), 220);
    return () => clearTimeout(id);
  }, [q, run]);

  useEffect(() => {
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) { setOpen(false); setMobileOpen(false); } };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const go = (rid) => { setOpen(false); setMobileOpen(false); setQ(""); setResults([]); navigate(`/candidates/${rid}`); };
  const onKey = (e) => {
    if (e.key === "Enter" && results[0]) go(results[0].registrationId);
    if (e.key === "Escape") { setQ(""); setOpen(false); setMobileOpen(false); }
  };
  const clear = () => { setQ(""); setResults([]); inputRef.current?.focus(); };
  const showList = open && q.trim().length > 0;

  return (
    <div className={`gs${mobileOpen ? " gs-mobile-open" : ""}`} ref={wrapRef} data-testid="global-search">
      <div className="search gs-pill">
        <Search size={16} />
        <input
          ref={inputRef}
          className="gs-input"
          data-testid="global-search-input"
          placeholder="Search name, HC ID, email or phone…"
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKey}
        />
        {q && <button className="gs-clear" onClick={clear} data-testid="global-search-clear" aria-label="Clear search"><X size={14} /></button>}
      </div>
      <button
        className="search-mini"
        data-testid="global-search-mobile"
        aria-label="Search candidates"
        onClick={() => { setMobileOpen((v) => !v); setTimeout(() => inputRef.current?.focus(), 50); }}
      >
        <Search size={18} />
      </button>

      {showList && (
        <div className="gs-results" data-testid="global-search-results">
          <div className="gs-scope">All candidates · every status</div>
          {loading && results.length === 0 && <div className="gs-empty" data-testid="global-search-loading">Searching…</div>}
          {!loading && results.length === 0 && <div className="gs-empty" data-testid="global-search-empty">No candidates match “{q.trim()}”.</div>}
          {results.map((r) => (
            <button key={r.registrationId} className="gs-item" data-testid={`global-search-result-${r.registrationId}`} onClick={() => go(r.registrationId)}>
              <span className="gs-name">{r.name}</span>
              <span className="gs-meta">
                <span className="gs-id">{r.registrationId}</span>
                {r.statusLabel && <span className="gs-status">{r.statusLabel}</span>}
                {r.employerName && <span className="gs-emp">{r.employerName}</span>}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
